import { lazy } from 'react';
import { fetchHostVendorCreds } from '../api/adminHook.js';

// ================================================================
// Meeting vendors — THE single place that knows about them.
//
//   hosting.vendor ── normalizeVendor() ──► which panel to render
//   credentials   ── fetchMeetingCredentials() ──► GET /cme/hostvendor/{vendor}/{meetingId}
//
// Valid vendor identifiers (use EXACTLY these, nowhere else):
//   'zoom' | 'realtimekit' | '100ms'
//
// To add a vendor: add an entry to MEETING_VENDORS + PANELS below.
// ================================================================

/** Canonical vendor identifiers (the {vendor} URL segment) + labels. */
export const MEETING_VENDORS = {
  zoom: { label: 'Zoom' },
  realtimekit: { label: 'RealtimeKit' },
  '100ms': { label: '100ms' },
};

// One lazy panel per vendor — a session only ever loads its own SDK chunk.
export const PANELS = {
  zoom: lazy(() => import('../zoom/ZoomHostPanel.jsx')),
  realtimekit: lazy(() => import('../realtimekit/RealtimeKitHostPanel.jsx')),
  '100ms': lazy(() => import('../100ms/HostPanel.jsx')),
};

// Spellings of `hosting.vendor` the admin/CMS may send for each non-Zoom vendor
// (compared lowercased, with spaces/dashes/underscores removed). The canonical
// id — and so the /cme/hostvendor/{vendor} URL segment — is 'realtimekit'.
// 'rtk' is the legacy id this project used before the rename; without it the
// session silently falls back to the Zoom panel.
const VENDOR_ALIASES = {
  realtimekit: ['realtimekit', 'rtk'],
  '100ms': ['100ms'],
};

/** Map `hosting.vendor` to a canonical identifier; unknown values fall back to 'zoom'. */
export function normalizeVendor(raw) {
  const value = String(raw ?? '').trim().toLowerCase().replace(/[\s_-]+/g, '');
  for (const [key, aliases] of Object.entries(VENDOR_ALIASES)) {
    if (aliases.includes(value)) return key;
  }
  return 'zoom';
}

/** First non-empty value among `keys`. */
function pick(object, keys) {
  for (const key of keys) {
    const value = object?.[key];
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return '';
}

// Where each vendor keeps its token, most specific first. The generic aliases
// come last on purpose: a payload that carries several token-ish fields must
// never hand 100ms a RealtimeKit token (or vice versa) just because the generic
// `token` field happened to be listed first.
const TOKEN_FIELDS = {
  realtimekit: ['realtimekit_token', 'authToken', 'auth_token', 'token'],
  '100ms': ['hundredms_token', 'authToken', 'auth_token', 'token'],
  zoom: ['zoom_signature', 'zoom_zak', 'authToken', 'auth_token', 'token'],
};

/**
 * Fetch credentials for one participant — every vendor joins the same way:
 * GET /cme/hostvendor/{vendor}/{meetingId} → hand the token to that vendor's SDK.
 * Field names differ per vendor, hence the accepted aliases.
 *
 * `responseKeys` is diagnostics only: it is the one way to see from the console
 * which fields the backend actually sent.
 */
export async function fetchMeetingCredentials(vendor, meetingId) {
  const data = await fetchHostVendorCreds(vendor, meetingId);
  const credentials = {
    // realtimekit: GET /cme/hostvendor/realtimekit/{id} → { realtimekit_token }
    // 100ms:       GET /cme/hostvendor/100ms/{id}       → { hundredms_token }
    authToken: pick(data, TOKEN_FIELDS[vendor] || TOKEN_FIELDS.realtimekit),
    userName: pick(data, ['userName', 'user_name', 'name']),
    responseKeys: data && typeof data === 'object' ? Object.keys(data) : [],
  };
  if (!credentials.authToken) {
    const label = MEETING_VENDORS[vendor]?.label || vendor;
    throw new Error(
      `No meeting credentials returned for this session. Is the session configured for ${label}?`,
    );
  }
  return credentials;
}
